import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Alert, Spin, Typography, Space } from "antd";
import {
  createPlaidLinkToken,
  exchangePlaidPublicToken,
  getPlaidApiBaseUrl,
} from "@/api/custom";
import {
  buildPlaidAppRedirectUrl,
  buildPlaidPublicRedirectUrl,
  consumePlaidRedirectQueryParams,
  currentLocationMatchesPlaidReturnPath,
  derivePlaidRedirectOutcome,
} from "./plaid-redirect-utils";
import { useAppDispatch } from "@/store/hooks";
import {
  // setGlobalPlaidData,
  // setPlaidError,
  // setAggregatedData,
  setConnectionError,
} from "@/store/features/plaid/plaidSlice";
import { fetchPlaidWealthSnapshot } from "@/store/features/plaid/plaid-thunks";

const { Text, Title } = Typography;

interface PlaidLinkModalProps {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  returnPath?: string;
}

const PlaidLinkModal: React.FC<PlaidLinkModalProps> = ({
  visible,
  onClose,
  onSuccess,
  returnPath,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<"initial" | "linking" | "success" | "error">(
    "initial",
  );
  const dispatch = useAppDispatch();

  const handleStartLinking = async () => {
    try {
      setLoading(true);
      setError(null);
      setStep("linking");

      // Create link token
      const linkTokenResponse = await createPlaidLinkToken();
      const linkToken = linkTokenResponse.link_token;

      // Always use the same Plaid API endpoint that the app is configured to call.
      const plaidApiBaseUrl = await getPlaidApiBaseUrl();
      const redirectUrl = buildPlaidAppRedirectUrl(
        window.location.origin,
        returnPath,
      );
      const plaidRedirectUrl = buildPlaidPublicRedirectUrl(
        plaidApiBaseUrl,
        linkToken,
        redirectUrl,
      );

      // Redirect in the same window (no popup)
      window.location.assign(plaidRedirectUrl);
    } catch (err: unknown) {
      console.error("Error starting Plaid linking:", err);
      setStep("error");
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to start bank connection process. Please try again.";
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handlePlaidSuccess = useCallback(
    async (publicToken?: string | null) => {
      try {
        setLoading(true);
        setError(null);
        setStep("linking");

        // Exchange public token for access token
        if (publicToken) {
          await exchangePlaidPublicToken(publicToken);
        }

        // Refresh Plaid data
        // const plaidData = await getPlaidAggregatedData();
        await dispatch(fetchPlaidWealthSnapshot({ range: "ALL" })).unwrap();
        dispatch(setConnectionError(null));

        setStep("success");
        setLoading(false);

        setTimeout(() => {
          onSuccess();
          onClose();
          setStep("initial");
        }, 2000);
      } catch (err: unknown) {
        console.error("Error exchanging Plaid token:", err);
        setStep("error");
        setError(
          err instanceof Error
            ? err.message
            : "Failed to complete bank connection. Please try again.",
        );
        setLoading(false);
      }
    },
    [dispatch, onClose, onSuccess],
  );

  // Check for Plaid success URL parameters
  useEffect(() => {
    if (
      !currentLocationMatchesPlaidReturnPath(
        window.location.pathname,
        returnPath,
      )
    ) {
      return;
    }
    const redirectParams = consumePlaidRedirectQueryParams();
    const outcome = derivePlaidRedirectOutcome(redirectParams);
    if (outcome.kind === "none") {
      return;
    }
    if (outcome.kind === "cancel") {
      setStep("error");
      setError(outcome.message);
      setLoading(false);
      return;
    }
    if (outcome.kind === "error") {
      setStep("error");
      setError(outcome.message);
      setLoading(false);
      return;
    }
    if (outcome.kind === "success") {
      void handlePlaidSuccess(outcome.publicToken);
    }
  }, [handlePlaidSuccess, returnPath]);

  const handleRetry = () => {
    setStep("initial");
    setError(null);
    setLoading(false);
  };

  const handleCancel = () => {
    setStep("initial");
    setError(null);
    setLoading(false);
    onClose();
  };

  const renderContent = () => {
    switch (step) {
      case "initial":
        return (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <Title level={4}>Connect Your Bank Accounts</Title>
            <Text>
              We use Plaid to securely connect your financial institutions. This
              allows us to provide you with a complete view of your financial
              picture.
            </Text>

            <div style={{ marginTop: "20px", textAlign: "left" }}>
              <Title level={5}>What happens next?</Title>
              <ul>
                <li>You'll be redirected to Plaid's secure platform</li>
                <li>Select your bank and log in with your credentials</li>
                <li>Choose which accounts to connect</li>
                <li>Return to see your connected accounts</li>
              </ul>
            </div>

            <div style={{ marginTop: "20px" }}>
              <Space>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleStartLinking}
                  loading={loading}
                >
                  Get Started
                </Button>
                <Button
                  type="default"
                  size="large"
                  onClick={handleCancel}
                  style={{
                    color: "#595959",
                    borderColor: "#d9d9d9",
                    background: "#fff",
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </div>
          </div>
        );

      case "linking":
        return (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
            <div style={{ marginTop: "20px" }}>
              <Title level={4}>Connecting Your Accounts</Title>
              <Text>
                Please complete the bank connection process in the new window.
                This window will automatically update when the process is
                complete.
              </Text>
            </div>
          </div>
        );

      case "success":
        return (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div
              style={{
                fontSize: "48px",
                color: "#52c41a",
                marginBottom: "20px",
              }}
            >
              ✓
            </div>
            <Title level={4}>Successfully Connected!</Title>
            <Text>
              Your bank accounts have been successfully connected. You can now
              view your external accounts in the wealth overview.
            </Text>
          </div>
        );

      case "error":
        return (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <Alert
              message="Connection Failed"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: "20px" }}
            />
            <div>
              <Space>
                <Button
                  type="primary"
                  onClick={handleRetry}
                  style={{ marginRight: "10px" }}
                >
                  Try Again
                </Button>
                <Button
                  type="default"
                  onClick={handleCancel}
                  style={{
                    color: "#595959",
                    borderColor: "#d9d9d9",
                    background: "#fff",
                    fontWeight: 500,
                  }}
                >
                  Cancel
                </Button>
              </Space>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title="Link External Account"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      centered
      destroyOnClose
    >
      {renderContent()}
    </Modal>
  );
};

export default PlaidLinkModal;
